import { type ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useEarlyAccess } from "@/components/EarlyAccessModal/context";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useMountEffect } from "@/hooks/useMountEffect";

interface NavLink {
	href: string;
	key: "problem" | "architecture" | "features" | "roadmap" | "comparison";
}

const NAV_LINKS: NavLink[] = [
	{ href: "#problem", key: "problem" },
	{ href: "#architecture", key: "architecture" },
	{ href: "#features", key: "features" },
	{ href: "#roadmap", key: "roadmap" },
	{ href: "#comparison", key: "comparison" },
];

/**
 * Toggle a class on `document.body` while `active` is true; remove it on
 * cleanup and whenever `active` flips off. Reusable sync-with-DOM hook.
 */
function useBodyClass(className: string, active: boolean): void {
	useEffect(() => {
		if (typeof document === "undefined") return;
		document.body.classList.toggle(className, active);
		return () => {
			document.body.classList.remove(className);
		};
	}, [className, active]);
}

export function Header(): ReactNode {
	const { t } = useTranslation();
	const { open: openWaitlist } = useEarlyAccess();
	const [isOpen, setIsOpen] = useState(false);
	const [isScrolled, setIsScrolled] = useState(false);

	useBodyClass("nav-open", isOpen);

	useMountEffect(() => {
		const onScroll = (): void => setIsScrolled(window.scrollY > 12);
		onScroll();
		window.addEventListener("scroll", onScroll, { passive: true });
		return () => window.removeEventListener("scroll", onScroll);
	});

	useMountEffect(() => {
		const onKey = (e: KeyboardEvent): void => {
			if (e.key === "Escape") setIsOpen(false);
		};
		document.addEventListener("keydown", onKey);
		return () => document.removeEventListener("keydown", onKey);
	});

	useMountEffect(() => {
		const mq = window.matchMedia("(min-width: 821px)");
		let lastWide = mq.matches;
		const onResize = (): void => {
			const wide = mq.matches;
			if (wide && !lastWide) setIsOpen(false);
			lastWide = wide;
		};
		window.addEventListener("resize", onResize, { passive: true });
		return () => window.removeEventListener("resize", onResize);
	});

	return (
		<header className={`nav${isScrolled ? " scrolled" : ""}${isOpen ? " open" : ""}`}>
			<div className="wrap nav-inner">
				<a className="brand" href="#top" aria-label={t("header.brandLabel")}>
					<span className="logo" aria-hidden="true">
						<svg viewBox="0 0 24 24" fill="none">
							<line x1="5" y1="6" x2="12" y2="12" stroke="var(--acc)" strokeWidth="1.4" />
							<line x1="19" y1="5" x2="12" y2="12" stroke="var(--acc)" strokeWidth="1.4" />
							<line x1="12" y1="12" x2="7" y2="19" stroke="var(--acc)" strokeWidth="1.4" />
							<line x1="12" y1="12" x2="18" y2="18" stroke="var(--acc2)" strokeWidth="1.4" />
							<circle cx="5" cy="6" r="2.4" fill="var(--acc)" />
							<circle cx="19" cy="5" r="2" fill="var(--acc)" />
							<circle cx="12" cy="12" r="2.8" fill="var(--acc-bright)" />
							<circle cx="7" cy="19" r="2" fill="var(--acc2)" />
							<circle cx="18" cy="18" r="2.2" fill="var(--acc2)" />
						</svg>
					</span>
					Graphless
				</a>
				<button
					type="button"
					className="nav-toggle"
					aria-label={isOpen ? t("header.ariaClose") : t("header.ariaOpen")}
					aria-expanded={isOpen}
					aria-controls="nav-menu"
					onClick={(e) => {
						e.stopPropagation();
						setIsOpen((v) => !v);
					}}
				>
					<span className="bar" />
					<span className="bar" />
					<span className="bar" />
				</button>
				<div className="nav-menu" id="nav-menu">
					<nav className="nav-links">
						{NAV_LINKS.map((l) => (
							<a key={l.href} href={l.href} onClick={() => setIsOpen(false)}>
								{t(`nav.${l.key}`)}
							</a>
						))}
					</nav>
					<div className="nav-cta">
						<button
							className="btn btn-primary"
							type="button"
							onClick={() => {
								setIsOpen(false);
								openWaitlist();
							}}
						>
							{t("header.ctaWaitlist")}
						</button>
						<LanguageSwitcher />
					</div>
				</div>
			</div>
		</header>
	);
}
